import { useCallback, useEffect } from 'react';

import { useParams, useSearchParams } from 'react-router';

import { useDisclosure } from '@chakra-ui/react';
import type { GridApi, IRowNode } from 'ag-grid-enterprise';
import Pdf from 'jspdf';
import autoTable, { applyPlugin, type HAlignType, type RowInput, type UserOptions } from 'jspdf-autotable';

import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_PARSE_FORMAT,
  filterNotDefined,
  formatDate,
  formatNumber,
  formatPercent,
  get,
  isDefined,
  isEmpty,
  isFunction,
  isNotDefined,
} from '@enterprise/core';

import type { MergePurgeView } from '@annotations/mergePurgeView';

import DateCellRenderer from '@components/grid/renderers/date-cell.renderer';
import NumberCellRenderer from '@components/grid/renderers/number-cell.renderer';
import PercentCellRenderer from '@components/grid/renderers/percent-cell.renderer';
import { useNthCalculatorStore } from '@components/nth-calculator/stores/nth-calculator-store';
import { useMergePurgeStore } from '@feat/reports/context';
import { useHasPermission } from '@hooks/authentication/has-permission.hook';

import type { PdfColumn } from './types/pdf-column.type';

applyPlugin(Pdf);

export const useMergePurgeHeaderConfiguration = (view: MergePurgeView) => {
  const { data: permissions } = useHasPermission(['dts-can-nth']);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const isNthCalculatorOpen = useMergePurgeStore(({ isNthCalculatorOpen }) => {
    return isNthCalculatorOpen;
  });
  const setIsNthCalculatorOpen = useMergePurgeStore(({ setIsNthCalculatorOpen }) => {
    return setIsNthCalculatorOpen;
  });
  const getGridApi = useMergePurgeStore(({ getGridApi }) => {
    return getGridApi;
  });
  const reports = useMergePurgeStore(({ reports }) => {
    return reports;
  });
  const [searchParams] = useSearchParams();
  const params = useParams<{ mailingId: string }>();
  const mailingId = Number(params.mailingId);
  const hasDraft = useNthCalculatorStore(({ drafts }) => {
    return Number.isFinite(mailingId) && isDefined(drafts[mailingId]);
  });

  const getColumns = useCallback(
    (api?: GridApi): PdfColumn[] => {
      const columns = (api ?? getGridApi(view))?.getAllDisplayedColumns() ?? [];

      return columns.map((column) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { cellRenderer, field, headerName } = column.getColDef();

        return {
          dataKey: field,
          header: headerName ?? field ?? '',
          isDate: isFunction(cellRenderer) && cellRenderer === DateCellRenderer,
          isNumber: isFunction(cellRenderer) && cellRenderer === NumberCellRenderer,
          isPercent: isFunction(cellRenderer) && cellRenderer === PercentCellRenderer,
        };
      });
    },
    [getGridApi, view]
  );

  const getColumnStyles = useCallback((columns: PdfColumn[]) => {
    return columns.reduce<Record<number, { halign: HAlignType }>>((accumulator, column, index) => {
      if (column.isNumber) {
        accumulator[index] = { halign: 'right' };
      }

      return accumulator;
    }, {});
  }, []);

  const getFilename = useCallback(
    (extension: string, reportView?: MergePurgeView) => {
      const date = formatDate(new Date(), 'yyyy-MM-dd');
      const mailing = searchParams.get('mailing')?.toLowerCase() ?? 'unknown';

      return `mp-${mailing}${isDefined(reportView) ? `-${reportView}` : ''}-${date}.${extension}`;
    },
    [searchParams]
  );

  const getRows = useCallback(
    (api?: GridApi) => {
      const columns = getColumns(api);
      const rows: IRowNode[] = [];

      (api ?? getGridApi(view))?.forEachNode((node) => {
        return rows.push(node);
      });

      return rows.map((row) => {
        return columns.map(({ dataKey, isDate, isNumber, isPercent }) => {
          if (isNotDefined(dataKey)) {
            return '';
          }

          const value = (api ?? getGridApi(view))?.getCellValue({ colKey: dataKey, rowNode: row }) as
            | string
            | undefined;

          if (isNotDefined(value)) {
            return '';
          }

          if (!isDate && !isNumber && !isPercent) {
            return value;
          }

          if (isPercent) {
            return formatPercent(Number.parseFloat(value));
          }

          if (isNumber) {
            return formatNumber(Number.parseInt(value, 10));
          }

          return formatDate(value, {
            format: DEFAULT_DATE_FORMAT,
            parseFormat: DEFAULT_PARSE_FORMAT,
          });
        });
      }) as RowInput[];
    },
    [getColumns, getGridApi, view]
  );

  const generatePdfOptions = useCallback(
    (columns: PdfColumn[], rows: RowInput[], startY: number): UserOptions => {
      return {
        body: rows,
        columnStyles: getColumnStyles(columns),
        didParseCell: ({ cell, column, section }) => {
          if (section === 'head') {
            const col = columns[column.index];

            if (isNotDefined(col)) {
              return;
            }

            const { isNumber, isPercent } = col;

            if (isPercent || isNumber) {
              cell.styles.halign = 'right';
            }
          }
        },
        head: [
          columns.map((col) => {
            return col.header;
          }),
        ],
        margin: { bottom: 5, left: 5, right: 5, top: 5 },
        pageBreak: 'auto',
        rowPageBreak: 'avoid',
        startY,
      };
    },
    [getColumnStyles]
  );

  const exportToCSV = useCallback(() => {
    getGridApi(view)?.exportDataAsCsv({
      allColumns: false,
      fileName: getFilename('csv', view),
    });
  }, [view, getGridApi, getFilename]);

  const exportToExcel = useCallback(() => {
    getGridApi(view)?.exportDataAsExcel({
      allColumns: false,
      fileName: getFilename('xlsx', view),
    });
  }, [view, getGridApi, getFilename]);

  const exportToPDF = useCallback(() => {
    const doc = new Pdf({
      orientation: 'landscape',
    });
    let filename: string;

    switch (view) {
      case 'full-report':
        {
          let index = 0;
          let startY = 10;

          filename = getFilename('pdf');

          for (const { name, view: reportView } of reports) {
            let gridApis: GridApi[] = [];

            switch (reportView) {
              case 'seedCount':
                gridApis = filterNotDefined([getGridApi('seedCount'), getGridApi('seedCountByMailCode')]);
                break;

              default:
                gridApis = filterNotDefined([getGridApi(reportView)]);
                break;
            }

            if (isEmpty(gridApis)) {
              continue;
            }

            for (const api of gridApis) {
              if (isNotDefined(api)) {
                continue;
              }

              const columns = getColumns(api);
              const rows = getRows(api);
              const noCells = rows.every((cell) => {
                return isEmpty(cell);
              });

              if (isEmpty(rows) || noCells) {
                continue;
              }

              if (index !== 0) {
                let increment = 0;

                // @ts-expect-error types not compatible with the jspdf-autotable applyPlugin
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if (isDefined(doc.lastAutoTable) && isDefined(doc.lastAutoTable.finalY)) {
                  // @ts-expect-error types not compatible with the jspdf-autotable applyPlugin
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  increment = (doc.lastAutoTable.finalY as number) + 5;
                }
                startY = startY + increment;
              }

              doc.text(name, 5, startY);

              autoTable(doc, generatePdfOptions(columns, rows, startY + 5));
            }

            index += 1;
          }
        }
        break;

      default:
        {
          const report = reports.find((r) => {
            return r.view === view;
          });

          filename = getFilename('pdf', view);

          doc.text(report?.name ?? 'Unknown Report', 5, 10);

          autoTable(doc, generatePdfOptions(getColumns(), getRows(), 15));
        }
        break;
    }

    doc.save(filename);
  }, [generatePdfOptions, getColumns, getFilename, getGridApi, getRows, reports, view]);

  const handleToggleNthCalculator = useCallback(() => {
    setIsNthCalculatorOpen(!isNthCalculatorOpen);
  }, [isNthCalculatorOpen, setIsNthCalculatorOpen]);

  useEffect(() => {
    setIsNthCalculatorOpen(isNthCalculatorOpen);
  }, [isNthCalculatorOpen, setIsNthCalculatorOpen]);

  return {
    canNth: get(permissions ?? {}, 'dts-can-nth', false),
    exportToCSV,
    exportToExcel,
    exportToPDF,
    handleToggleNthCalculator,
    hasDraft,
    isOpen,
    onClose,
    onOpen,
  };
};
